/**
 * TWDayPage — view/edit Word of God entry for a specific date.
 * Route: /tw/:date
 * Passcode-gated editing (any of 4 codes).
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Edit2, Save, X,
  Copy, Check, BookOpen, Image, Trash2, Lock,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getTWEntry, saveTWEntry, uploadTWFlyer, deleteTWFlyer,
  buildCopyText, checkTWPasscode, todayId,
  TW_SESSION_KEY,
} from '@/services/twService';
import type { TWEntry } from '@/services/twService';
import { cn } from '@/utils/cn';

// ── OTP-style Passcode gate ────────────────────────────────────────────────────

function PasscodeModal({ onUnlock, onClose }: { onUnlock: () => void; onClose: () => void }) {
  const [digits, setDigits] = useState<string[]>(['','','','','','']);
  const [error,  setError]  = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => { inputRefs.current[0]?.focus(); }, []);

  const submit = useCallback((code: string) => {
    if (checkTWPasscode(code)) {
      sessionStorage.setItem(TW_SESSION_KEY, '1');
      onUnlock();
    } else {
      setError(true);
      setDigits(['','','','','','']);
      setTimeout(() => {
        setError(false);
        inputRefs.current[0]?.focus();
      }, 1000);
    }
  }, [onUnlock]);

  const handleChange = (idx: number, val: string) => {
    const ch = val.replace(/\D/g, '').slice(-1);
    const next = digits.map((d, i) => i === idx ? ch : d);
    setDigits(next);
    if (ch) {
      if (idx < 5) {
        inputRefs.current[idx + 1]?.focus();
      } else {
        // All 6 digits entered — auto-submit
        submit(next.join(''));
      }
    }
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setDigits(pasted.split(''));
      submit(pasted);
    } else {
      const next = ['','','','','',''];
      pasted.split('').forEach((ch, i) => { next[i] = ch; });
      setDigits(next);
      inputRefs.current[pasted.length]?.focus();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-amber-500" />
            <p className="font-bold text-gray-900">Enter Passcode to Edit</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        {/* 6 OTP digit boxes */}
        <div className={cn('flex gap-2 justify-center', error && 'animate-[shake_0.4s_ease-in-out]')}>
          {digits.map((d, idx) => (
            <input
              key={idx}
              ref={el => { inputRefs.current[idx] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={e => handleChange(idx, e.target.value)}
              onKeyDown={e => handleKeyDown(idx, e)}
              onPaste={idx === 0 ? handlePaste : undefined}
              className={cn(
                'w-11 h-14 text-center text-xl font-bold rounded-xl border-2 focus:outline-none transition-all caret-transparent',
                error
                  ? 'border-red-400 bg-red-50 text-red-600'
                  : d
                  ? 'border-amber-400 bg-amber-50 text-amber-700'
                  : 'border-gray-200 bg-white text-gray-900 focus:border-amber-400'
              )}
            />
          ))}
        </div>
        {error && <p className="text-center text-sm text-red-500 mt-3">Incorrect passcode</p>}
        <p className="text-center text-xs text-gray-400 mt-3">Enter 6-digit code — submits automatically</p>
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function prevDateId(dateId: string): string {
  const d = new Date(dateId + 'T00:00:00');
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function nextDateId(dateId: string): string {
  const d = new Date(dateId + 'T00:00:00');
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function formatDisplayDate(dateId: string): string {
  const d = new Date(dateId + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long', year:'numeric' });
}

// ── Share helpers ──────────────────────────────────────────────────────────────

async function fetchImageFile(url: string, dateId: string): Promise<File> {
  const res  = await fetch(url);
  const blob = await res.blob();
  return new File([blob], `word-of-god-${dateId}.jpg`, { type: blob.type });
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function TWDayPage() {
  const { date }    = useParams<{ date: string }>();
  const navigate    = useNavigate();
  const dateId      = date ?? todayId();
  const today       = todayId();
  const isFuture    = dateId > today;

  const [entry,        setEntry]        = useState<TWEntry | null>(null);
  const [loading,      setLoading]      = useState(true);
  const [editing,      setEditing]      = useState(false);
  const [showPasscode, setShowPasscode] = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [copied,       setCopied]       = useState(false);
  const [imgCopied,    setImgCopied]    = useState(false);
  const [uploading,    setUploading]    = useState(false);
  const [dragOver,     setDragOver]     = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Edit form state
  const [engQ, setEngQ] = useState('');
  const [malQ, setMalQ] = useState('');
  const [hinQ, setHinQ] = useState('');
  const [ref_,  setRef_]  = useState('');

  useEffect(() => {
    setLoading(true);
    setEditing(false);
    getTWEntry(dateId).then(e => {
      setEntry(e);
      if (e) { setEngQ(e.englishQuote); setMalQ(e.malayalamQuote); setHinQ(e.hindiQuote); setRef_(e.reference); }
      else { setEngQ(''); setMalQ(''); setHinQ(''); setRef_(''); }
      setLoading(false);
    });
  }, [dateId]);

  // ── Paste-to-upload listener ───────────────────────────────────────────────
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (!editing) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) handleFileUpload(file);
          break;
        }
      }
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, entry]);

  const isEditUnlocked = () => sessionStorage.getItem(TW_SESSION_KEY) === '1';
  const handleEditClick = () => {
    if (isEditUnlocked()) setEditing(true);
    else setShowPasscode(true);
  };
  const handleUnlock = () => { setShowPasscode(false); setEditing(true); };

  const handleSave = async () => {
    if (!dateId) return;
    setSaving(true);
    const updated: Omit<TWEntry, 'date' | 'updatedAt'> = {
      englishQuote:   engQ.trim(),
      malayalamQuote: malQ.trim(),
      hindiQuote:     hinQ.trim(),
      reference:      ref_.trim(),
      flyerUrl:       entry?.flyerUrl ?? null,
      flyerPath:      entry?.flyerPath ?? null,
    };
    await saveTWEntry(dateId, updated);
    setEntry(prev => prev
      ? { ...prev, ...updated }
      : { date: dateId, ...updated, updatedAt: new Date() }
    );
    toast.success('Saved');
    setEditing(false);
    setSaving(false);
  };

  const handleCancelEdit = () => {
    if (entry) { setEngQ(entry.englishQuote); setMalQ(entry.malayalamQuote); setHinQ(entry.hindiQuote); setRef_(entry.reference); }
    setEditing(false);
  };

  const handleCopyText = async () => {
    if (!entry) return;
    const text = buildCopyText(entry);
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Text copied!');
  };

  const handleCopyImage = async () => {
    if (!entry?.flyerUrl) return;
    try {
      const file = await fetchImageFile(entry.flyerUrl, dateId);
      await navigator.clipboard.write([
        new ClipboardItem({ [file.type]: Promise.resolve(new Blob([await file.arrayBuffer()], { type: file.type })) }),
      ]);
      setImgCopied(true);
      setTimeout(() => setImgCopied(false), 2000);
      toast.success('Image copied!');
    } catch {
      toast.error('Could not copy image — try long-pressing the flyer instead');
    }
  };

  const handleShareFlyer = async (platform: 'whatsapp' | 'instagram' | 'facebook') => {
    if (!entry?.flyerUrl) return;
    try {
      const file = await fetchImageFile(entry.flyerUrl, dateId);
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: 'Word of God — Angels Army Ministry',
          text:  entry.reference || '',
          files: [file],
        });
        return;
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
    }
    // Fallback for desktop
    if (platform === 'whatsapp') {
      window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(entry.flyerUrl)}`, '_blank');
    } else if (platform === 'facebook') {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(entry.flyerUrl)}`, '_blank');
    } else {
      // Instagram has no direct web share URL — open image for manual share
      window.open(entry.flyerUrl, '_blank');
      toast('Open in Instagram and share from your gallery', { icon: '📸' });
    }
  };

  // ── File upload (shared by file picker, paste, drag-drop) ──────────────────
  const handleFileUpload = async (file: File) => {
    if (!dateId) return;
    setUploading(true);
    try {
      if (entry?.flyerPath) await deleteTWFlyer(entry.flyerPath);
      const { url, path } = await uploadTWFlyer(dateId, file);
      await saveTWEntry(dateId, {
        englishQuote:   entry?.englishQuote   ?? engQ,
        malayalamQuote: entry?.malayalamQuote ?? malQ,
        hindiQuote:     entry?.hindiQuote     ?? hinQ,
        reference:      entry?.reference      ?? ref_,
        flyerUrl:  url,
        flyerPath: path,
      });
      setEntry(prev => prev
        ? { ...prev, flyerUrl: url, flyerPath: path }
        : { date: dateId, englishQuote: engQ, malayalamQuote: malQ, hindiQuote: hinQ, reference: ref_, flyerUrl: url, flyerPath: path, updatedAt: new Date() }
      );
      toast.success('Flyer uploaded');
    } catch {
      toast.error('Upload failed');
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleUploadFlyer = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
  };

  const handleDeleteFlyer = async () => {
    if (!entry?.flyerPath || !confirm('Remove flyer image?')) return;
    await deleteTWFlyer(entry.flyerPath);
    await saveTWEntry(dateId, { ...entry, flyerUrl: null, flyerPath: null });
    setEntry(prev => prev ? { ...prev, flyerUrl: null, flyerPath: null } : prev);
    toast.success('Flyer removed');
  };

  // ── Drag-and-drop ──────────────────────────────────────────────────────────
  const handleDragOver  = (e: React.DragEvent) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) handleFileUpload(file);
  };

  const taCls = 'w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none';

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {showPasscode && <PasscodeModal onUnlock={handleUnlock} onClose={() => setShowPasscode(false)} />}

      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={() => navigate('/tw')} className="p-1.5 rounded-xl hover:bg-gray-100 transition-colors">
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-1.5">
            <BookOpen className="h-4 w-4 text-amber-500" />
            <p className="text-sm font-bold text-gray-900">Word of God</p>
          </div>
          <p className="text-xs text-gray-500">{formatDisplayDate(dateId)}</p>
        </div>
        {!editing && (
          <button onClick={handleEditClick}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
            <Edit2 className="h-3.5 w-3.5" /> Edit
          </button>
        )}
        {editing && (
          <div className="flex gap-2">
            <button onClick={handleCancelEdit}
              className="p-1.5 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50">
              <X className="h-4 w-4" />
            </button>
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-500 text-white text-xs font-semibold active:scale-95 transition-all disabled:opacity-60">
              <Save className="h-3.5 w-3.5" />{saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </div>

      {/* Date navigation */}
      <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-50">
        <button onClick={() => navigate(`/tw/${prevDateId(dateId)}`)}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 transition-colors py-1">
          <ChevronLeft className="h-3.5 w-3.5" /> Previous day
        </button>
        {dateId !== today && (
          <button onClick={() => navigate(`/tw/${today}`)}
            className="text-xs font-semibold text-amber-600">Today</button>
        )}
        <button onClick={() => navigate(`/tw/${nextDateId(dateId)}`)}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 transition-colors py-1">
          Next day <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      {isFuture && (
        <div className="max-w-lg mx-auto px-4 pt-3">
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-700 font-medium text-center">
            Future date — content saved but not shown in gallery until the date arrives
          </div>
        </div>
      )}

      <div className="max-w-lg mx-auto px-4 py-5 space-y-4">

        {/* Flyer image */}
        {entry?.flyerUrl && (
          <div className="relative rounded-2xl overflow-hidden shadow-sm">
            <img src={entry.flyerUrl} alt="Flyer" className="w-full object-cover" />
            {editing && (
              <button onClick={handleDeleteFlyer}
                className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-xl shadow">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        {/* Quotes card */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-4">

          {editing ? (
            <>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">English</label>
                <textarea rows={4} value={engQ} onChange={e => setEngQ(e.target.value)}
                  placeholder="Enter English Bible quote…" className={taCls} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Malayalam</label>
                <textarea rows={4} value={malQ} onChange={e => setMalQ(e.target.value)}
                  placeholder="മലയാളം ബൈബിൾ വാക്യം…" className={taCls} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Hindi</label>
                <textarea rows={4} value={hinQ} onChange={e => setHinQ(e.target.value)}
                  placeholder="हिंदी बाइबिल वचन…" className={taCls} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Reference</label>
                <input value={ref_} onChange={e => setRef_(e.target.value)}
                  placeholder="e.g. Habakkuk 3:17"
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400" />
              </div>
            </>
          ) : entry && (entry.englishQuote || entry.malayalamQuote || entry.hindiQuote) ? (
            <>
              {entry.englishQuote && (
                <div>
                  <p className="text-[10px] font-bold text-amber-500 uppercase tracking-widest mb-1">English</p>
                  <p className="text-sm text-gray-800 leading-relaxed">{entry.englishQuote}</p>
                </div>
              )}
              {entry.malayalamQuote && (
                <div className="pt-2 border-t border-gray-50">
                  <p className="text-[10px] font-bold text-amber-500 uppercase tracking-widest mb-1">Malayalam</p>
                  <p className="text-sm text-gray-800 leading-relaxed">{entry.malayalamQuote}</p>
                </div>
              )}
              {entry.hindiQuote && (
                <div className="pt-2 border-t border-gray-50">
                  <p className="text-[10px] font-bold text-amber-500 uppercase tracking-widest mb-1">Hindi</p>
                  <p className="text-sm text-gray-800 leading-relaxed">{entry.hindiQuote}</p>
                </div>
              )}
              {entry.reference && (
                <div className="pt-2 border-t border-gray-50">
                  <p className="text-xs font-bold text-gray-500 text-right">— {entry.reference}</p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <BookOpen className="h-8 w-8 mx-auto mb-2 text-gray-200" />
              <p className="text-sm">No entry for this date.</p>
              <button onClick={handleEditClick}
                className="mt-2 text-sm font-semibold text-amber-600 hover:underline">
                Add Bible verse →
              </button>
            </div>
          )}
        </div>

        {/* Action buttons */}
        {entry && (entry.englishQuote || entry.malayalamQuote || entry.hindiQuote || entry.flyerUrl) && !editing && (
          <div className="space-y-2">
            {/* Copy text */}
            {(entry.englishQuote || entry.malayalamQuote || entry.hindiQuote) && (
              <button onClick={handleCopyText}
                className={cn(
                  'w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all active:scale-95',
                  copied
                    ? 'bg-emerald-50 text-emerald-600 border border-emerald-200'
                    : 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100'
                )}>
                {copied ? <><Check className="h-4 w-4" /> Copied!</> : <><Copy className="h-4 w-4" /> Copy Bible Text</>}
              </button>
            )}

            {/* Copy image */}
            {entry.flyerUrl && (
              <button onClick={handleCopyImage}
                className={cn(
                  'w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all active:scale-95',
                  imgCopied
                    ? 'bg-emerald-50 text-emerald-600 border border-emerald-200'
                    : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
                )}>
                {imgCopied ? <><Check className="h-4 w-4" /> Image Copied!</> : <><Image className="h-4 w-4" /> Copy Image</>}
              </button>
            )}

            {/* Share flyer buttons */}
            {entry.flyerUrl && (
              <div className="grid grid-cols-3 gap-2">
                {/* WhatsApp */}
                <button onClick={() => handleShareFlyer('whatsapp')}
                  className="flex flex-col items-center justify-center gap-1.5 py-3 px-2 rounded-xl text-xs font-semibold bg-[#25D366] text-white hover:opacity-90 active:scale-95 transition-all">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp
                </button>

                {/* Instagram */}
                <button onClick={() => handleShareFlyer('instagram')}
                  className="flex flex-col items-center justify-center gap-1.5 py-3 px-2 rounded-xl text-xs font-semibold text-white active:scale-95 transition-all hover:opacity-90"
                  style={{ background: 'linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)' }}>
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                  Instagram
                </button>

                {/* Facebook */}
                <button onClick={() => handleShareFlyer('facebook')}
                  className="flex flex-col items-center justify-center gap-1.5 py-3 px-2 rounded-xl text-xs font-semibold bg-[#1877F2] text-white hover:opacity-90 active:scale-95 transition-all">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Facebook
                </button>
              </div>
            )}
          </div>
        )}

        {/* Upload flyer — shown in edit mode */}
        {editing && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Flyer Image (1080 × 1920)</p>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUploadFlyer} />

            {/* Drop zone */}
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={cn(
                'w-full flex flex-col items-center justify-center gap-2 py-6 rounded-xl border-2 border-dashed cursor-pointer transition-colors',
                dragOver
                  ? 'border-amber-400 bg-amber-50 text-amber-600'
                  : 'border-gray-200 text-gray-500 hover:border-amber-300 hover:text-amber-600',
                uploading && 'opacity-60 pointer-events-none'
              )}
            >
              <Image className="h-6 w-6" />
              {uploading ? (
                <p className="text-sm font-semibold">Uploading…</p>
              ) : dragOver ? (
                <p className="text-sm font-semibold">Drop to upload</p>
              ) : (
                <>
                  <p className="text-sm font-semibold">{entry?.flyerUrl ? 'Replace Flyer' : 'Upload Flyer'}</p>
                  <p className="text-xs text-gray-400">Tap · Drag &amp; drop · Paste (Ctrl+V)</p>
                </>
              )}
            </div>

            {entry?.flyerUrl && (
              <p className="text-xs text-emerald-600 text-center mt-2 font-medium">✓ Flyer uploaded</p>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
